import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Form, Modal, Alert, Row, Col } from "react-bootstrap";
import {
  createCourse,
  updateCourse,
  clearError,
} from "../../redux/slices/courseSlice";
import { fetchUniversities } from "../../redux/slices/universitySlice";
import { toast } from "react-toastify";
import { FaRegTrashAlt } from "react-icons/fa";

const CourseModal = ({ show, onHide, course = null }) => {
  const dispatch = useDispatch();
  const isEditMode = !!course;

  const { isCreating, isUpdating, error, courses = [] } = useSelector((state) => state.courses);
  const { universities } = useSelector((state) => state.universities);

  const [formData, setFormData] = useState({
    university_id: "",
    course_name: "",
  });

  const [imageFile, setImageFile] = useState(null);
  const [existingImageUrl, setExistingImageUrl] = useState("");
  const [deleteImage, setDeleteImage] = useState("false");
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    dispatch(fetchUniversities());
  }, [dispatch]);

  useEffect(() => {
    if (show) {
      if (isEditMode && course) {
        setFormData({
          university_id: course.university_id || "",
          course_name: course.course_name || "",
        });
        setExistingImageUrl(course.image_url || "");
      } else {
        setFormData({
          university_id: "",
          course_name: "",
        });
        setExistingImageUrl("");
      }
      setImageFile(null);
      setDeleteImage("false");
      setValidationErrors({});
    }
  }, [show, isEditMode, course]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setDeleteImage("false");
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    if (isEditMode) {
      setExistingImageUrl("");
      setDeleteImage("true");
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.university_id) {
      errors.university_id = "University selection is required";
    }
    if (!formData.course_name.trim()) {
      errors.course_name = "Course name is required";
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;



    const submitData = new FormData();
    submitData.append("university_id", formData.university_id);
    submitData.append("course_name", formData.course_name.trim());
    if (imageFile) {
      submitData.append("image", imageFile);
    }
    if (isEditMode) {
      submitData.append("deleteImage", deleteImage);
    }

    try {
      if (isEditMode) {
        await dispatch(updateCourse({ id: course.id, formData: submitData })).unwrap();
      } else {
        await dispatch(createCourse(submitData)).unwrap();
      }
      onHide();
    } catch (err) {
      console.error("Course operation failed:", err);
      toast.error(err || "Something went wrong");
    }
  };

  const isSubmitting = isCreating || isUpdating;

  return (
    <Modal show={show} onHide={onHide} centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditMode ? "Update Course" : "Create New Course"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => dispatch(clearError())}>
              {error}
            </Alert>
          )}

          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>
                  University <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  name="university_id"
                  value={formData.university_id}
                  onChange={handleInputChange}
                  isInvalid={!!validationErrors.university_id}
                  disabled={isSubmitting}
                >
                  <option value="">Select University</option>
                  {universities.map((uni) => (
                    <option key={uni.id} value={uni.id}>
                      {uni.university_name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {validationErrors.university_id}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>
                  Course Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="course_name"
                  placeholder="Enter course name"
                  value={formData.course_name}
                  onChange={handleInputChange}
                  isInvalid={!!validationErrors.course_name}
                  disabled={isSubmitting}
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors.course_name}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Course Image</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  disabled={isSubmitting}
                />
                {(imageFile || existingImageUrl) && (
                  <div className="mt-3 p-2 border rounded d-flex align-items-center gap-3">
                    <img
                      src={imageFile ? URL.createObjectURL(imageFile) : existingImageUrl}
                      alt="Preview"
                      style={{
                        width: "50px",
                        height: "50px",
                        objectFit: "cover",
                        borderRadius: "4px",
                      }}
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                    <div className="flex-grow-1 overflow-hidden">
                      <small className="text-muted d-block text-truncate">
                        {imageFile ? imageFile.name : "Current Image"}
                      </small>
                    </div>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={handleRemoveImage}
                      disabled={isSubmitting}
                    >
                      <FaRegTrashAlt />
                    </Button>
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="btn-outline-danger-custom"
            onClick={onHide}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            className="btn-outline-success"
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? "Processing..."
              : isEditMode
                ? "Update Course"
                : "Create Course"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CourseModal;
